import {
  TournamentStore,
  type Tournament,
  type TournamentEvent,
  type TournamentEventBody,
} from '@ctd/core';
import { recordFromEvents, recordOf, type TournamentRepo } from './repo';

export interface SessionSnapshot {
  readonly tournament: Tournament;
  readonly events: readonly TournamentEvent[];
}

/** The device name written into each event's actor field. */
export const LOCAL_ACTOR = 'this device';

/**
 * An open tournament: the core event store plus persistence. Every change
 * (dispatch or undo) is written back to the repository in order. The class
 * also implements the subscribe/getSnapshot pair React's
 * useSyncExternalStore expects.
 */
export class TournamentSession {
  readonly store: TournamentStore;
  #snapshot: SessionSnapshot;
  #listeners = new Set<() => void>();
  #saving: Promise<void> = Promise.resolve();
  #saveError: unknown = null;

  constructor(
    events: readonly TournamentEvent[],
    private readonly repo: TournamentRepo,
    private readonly onSaveError: (e: unknown) => void = () => {},
  ) {
    this.store = new TournamentStore(events);
    this.#snapshot = this.#takeSnapshot();
    this.store.subscribe(() => this.#changed());
  }

  get id(): string {
    return this.store.state.id;
  }

  /** Validate and append an event. Throws DomainError when it does not apply. */
  dispatch(body: TournamentEventBody): TournamentEvent {
    return this.store.dispatch(body, LOCAL_ACTOR);
  }

  /** Remove the last event (never the creation). */
  undo(): TournamentEvent | null {
    return this.store.undo();
  }

  /** Resolves when every change so far has been saved. */
  flush(): Promise<void> {
    return this.#saving;
  }

  get lastSaveError(): unknown {
    return this.#saveError;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  getSnapshot = (): SessionSnapshot => this.#snapshot;

  #takeSnapshot(): SessionSnapshot {
    return { tournament: this.store.state, events: [...this.store.events] };
  }

  #changed(): void {
    this.#snapshot = this.#takeSnapshot();
    const record = recordOf(this.#snapshot.tournament, this.#snapshot.events);
    this.#saving = this.#saving
      .then(() => this.repo.put(record))
      .then(
        () => {
          this.#saveError = null;
        },
        (e: unknown) => {
          this.#saveError = e;
          this.onSaveError(e);
        },
      );
    for (const l of this.#listeners) l();
  }
}

/** Start a new tournament log and save it. */
export async function createTournament(
  repo: TournamentRepo,
  created: Extract<TournamentEventBody, { type: 'TournamentCreated' }>,
): Promise<string> {
  const store = new TournamentStore();
  store.dispatch(created, LOCAL_ACTOR);
  await repo.put(recordFromEvents(store.events));
  return created.id;
}
